import { useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

import { PositionsFormValues, positionFieldNames } from '../models/positions-form-values';
import { useInvoiceDetailContext } from '../../context/invoice-detail-context';

/**
 * Tells the head how many positions the document carries, and how to empty it.
 *
 * The head asks before it changes the supplier, and for that it has to know
 * whether anything was picked from the supplier it has now (Prüfbericht Roland
 * Niedermayr, 28.09.2026, Punkt 5). Head and positions are two forms, so one
 * cannot read the other directly.
 *
 * It draws nothing.
 */
/** Whether a position is more than an empty row waiting for its article. */
const carriesSomething = (item: unknown): boolean => {
    const row = item as { product?: unknown; name?: Record<string, string>; title?: string };

    return Boolean(row.product) || Boolean(row.title) || Object.values(row.name ?? {}).some(Boolean);
};

export const PaloInvoicePositionsWatcher = (): null => {
    const formik = useFormikContext<PositionsFormValues>();
    const { setPositionCount, setRemoveAllPositions } = useInvoiceDetailContext();
    // A row that is still empty is nothing to warn about when the supplier
    // changes — it carries neither article nor price.
    const count = (formik.values.allItems ?? []).filter(carriesSomething).length;
    // The callback outlives a render, so it reads the form through a ref
    // instead of closing over the values of the render it was made in.
    const current = useRef(formik);

    current.current = formik;

    const removeAll = useCallback(() => current.current.setFieldValue(positionFieldNames.allItems, []), []);

    useEffect(() => {
        setPositionCount?.(count);
    }, [count, setPositionCount]);

    useEffect(() => {
        setRemoveAllPositions?.(removeAll);

        return () => setRemoveAllPositions?.(undefined);
    }, [removeAll, setRemoveAllPositions]);

    return null;
};
